/* eslint-disable max-statements */

import { Box, Checkbox, Flex, Group, Paper, ScrollArea, UnstyledButton } from '@mantine/core';
import { FC, useEffect, useMemo, useRef, useState } from 'react';

import { LISTFILTER_RANDOM_ID_CHAR_COUNT, LISTFILTER_RANDOM_ID_CONVERSION_BASE } from '@constants';
import { DropdownArrow } from '../DropdownArrow';
import classes from './ListFilterDropdown.module.css';

enum ListFilterIndeterminateEnum {
  NONE = 'NONE',
  SOME = 'SOME',
  ALL = 'ALL',
}

export interface ListFilterDropdownValue {
  label: string;
  checked: boolean;
  'data-pw'?: string;
  icon?: React.ReactNode;
}

export type ListFilterDropdownElements = Record<string, ListFilterDropdownValue>;

export interface ListFilterChangeEvent {
  id: string;
  checked: boolean;
}

export interface ListFilterDropdownProps {
  title: string;
  values?: ListFilterDropdownElements;
  selectedValue?: ListFilterDropdownElements;
  onChange: (event: ListFilterChangeEvent) => void;
  onHide?: () => void;
  'data-pw'?: string;
  hideShowAll?: boolean;
}

export const ListFilterDropdown: FC<ListFilterDropdownProps> = (props) => {
  const { values, onChange, hideShowAll = false } = props;

  const [isOpen, setIsOpen] = useState(false);

  const [elements, setElements] = useState<ListFilterDropdownElements>({});
  const [allChecked, setAllChecked] = useState<ListFilterIndeterminateEnum>();
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (values) {
      const builtElement: ListFilterDropdownElements = {};
      Object.keys(values).forEach((key) => {
        builtElement[key] = { ...values[key] };
        builtElement[key].checked = !!values[key].checked;
      });
      setElements(builtElement);
    }
  }, [values]);

  const setAllElements = (checked: boolean) => {
    const elementsCopy = { ...elements };
    Object.keys(elementsCopy).forEach((elementKey) => {
      elementsCopy[elementKey].checked = checked;
    });
    setElements(elementsCopy);
    onChange({ id: undefined, checked });
    isFirstRun.current = false;
  };

  useEffect(() => {
    let indeterminateState: ListFilterIndeterminateEnum = ListFilterIndeterminateEnum.NONE;
    const countSelected = Object.keys(elements).filter((id) => elements[id].checked).length;
    const totalElements = Object.keys(elements).length;

    if (totalElements === 0) {
      return;
    }

    if (countSelected < totalElements) {
      indeterminateState = ListFilterIndeterminateEnum.SOME;
    } else if (countSelected === totalElements) {
      indeterminateState = ListFilterIndeterminateEnum.ALL;
    }

    if (indeterminateState !== allChecked) {
      setAllChecked(indeterminateState);
    }
  }, [elements]);

  useEffect(() => {
    switch (allChecked) {
      case ListFilterIndeterminateEnum.ALL:
        setAllElements(true);
        break;
      case ListFilterIndeterminateEnum.NONE:
        setAllElements(false);
        break;
      default:
        break;
    }
  }, [allChecked]);

  const change = (id: string) => {
    const newValue: ListFilterChangeEvent = { id, checked: !elements[id].checked };
    onChange(newValue);
    setElements({
      ...elements,
      [id]: {
        ...elements[id],
        checked: newValue.checked,
      },
    });
  };

  const ref = useRef(null);

  const id = useMemo(() => `id_${(Math.random() + 1).toString(LISTFILTER_RANDOM_ID_CONVERSION_BASE).substring(LISTFILTER_RANDOM_ID_CHAR_COUNT)}`, []);

  const onClickOutside = () => {
    setIsOpen(false);
    if (props.onHide) {
      props.onHide();
    }
  };

  const handleClickOutside = (event: any) => {
    if (ref.current && !ref.current.contains(event.target) && !event.target.closest(`#${id}`)) {
      onClickOutside?.();
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);
    return () => {
      document.removeEventListener('click', handleClickOutside, true);
    };
  });

  return (
    <Flex pos={'relative'} pl={20}>
      <UnstyledButton className={classes.dropDownButton} data-pw={props['data-pw']} id={id} onClick={() => setIsOpen(!isOpen)}>
        {props.title}
        <DropdownArrow isOpen={isOpen} />
      </UnstyledButton>

      {isOpen && (
        <Paper className={classes.dropDownList} ref={ref}>
          <ScrollArea h={Object.keys(elements).length >= 14 ? '450px' : 'fit-content'} offsetScrollbars="y">
            {!hideShowAll && (
              <Box
                className={classes.dropDownItem}
                data-pw={`${props['data-pw']}-all`}
                onMouseDown={() =>
                  setAllChecked(
                    [ListFilterIndeterminateEnum.NONE, ListFilterIndeterminateEnum.SOME].includes(allChecked)
                      ? ListFilterIndeterminateEnum.ALL
                      : ListFilterIndeterminateEnum.NONE,
                  )
                }
              >
                <Checkbox
                  checked={allChecked === ListFilterIndeterminateEnum.ALL}
                  label="Show all"
                  readOnly
                  styles={{
                    label: { cursor: 'pointer' },
                    input: { cursor: 'pointer' },
                  }}
                />
              </Box>
            )}
            {Object.keys(elements).map((key) => {
              return (
                <Box className={classes.dropDownItem} data-pw={elements[key]['data-pw']} key={key} onMouseDown={() => change(key)}>
                  <Checkbox
                    checked={elements[key].checked}
                    label={
                      <Group gap={8}>
                        <span>{elements[key].label}</span>
                        {elements[key].icon}
                      </Group>
                    }
                    readOnly
                    styles={{
                      body: { alignItems: 'center' },
                      label: { cursor: 'pointer' },
                      input: { cursor: 'pointer' },
                    }}
                  />
                </Box>
              );
            })}
          </ScrollArea>
        </Paper>
      )}
    </Flex>
  );
};
